import { diasHastaVencimiento, type EstadoVencimiento } from "@/lib/vencimiento";

/**
 * Generador ÚNICO de la celda de vencimiento (DS §4bis.1).
 *
 * El DS lo pide con todas las letras: "un solo generador de datos (`celdaVig`)"
 * para Documentos Empresa, ficha de trabajador, EPP, vehículos y maquinaria.
 * Antes había CUATRO, y ya decían cosas distintas del mismo documento:
 *
 *   - `RecursoDocumentosPanel` (vehículo / maquinaria) — "en 44 días · 2 avisos"
 *   - `EPP` — igual, pero con su propio armado del texto
 *   - `DocumentacionGeneralPanel` (trabajador) — decía sólo "vigente": ni los
 *     días ni los avisos, y sin la advertencia ámbar cuando no hay ninguno
 *   - `CapacitacionesDocumentosPanel` (empresa) — sólo la fecha, una línea
 *
 * La divergencia no era cosmética. Un documento de trabajador con fecha y sin
 * recordatorios se veía EXACTAMENTE igual que uno con recordatorios, y el único
 * correo que dispara es el del día del vencimiento —el que no se puede apagar
 * (`App\Support\VentanasAviso::DIA_VENCIMIENTO`)—, o sea que nadie se entera
 * mientras todavía se puede renovar. Esa es la omisión que el ámbar denuncia.
 *
 * Por qué las dos líneas van SIEMPRE juntas: la celda contesta entera la
 * pregunta que la origina, "¿cuándo vence y me van a avisar?". Contestar sólo
 * la primera mitad deja al usuario creyendo que el sistema le va a avisar.
 */

/** Estado resuelto de la celda. Deriva de "no exigible" → fecha → plazo, EN ESE
 * ORDEN: un documento que la empresa no exige no está vencido, está fuera. */
export type EstadoCeldaVigencia =
  | "no_exigible"
  | "sin_archivo"
  | "permanente"
  | "vigente"
  | "por_vencer"
  | "vencido";

export interface EntradaCeldaVigencia {
  /** `YYYY-MM-DD` o `null`. Null = el usuario eligió "No vence" en el modal de
   * vigencia, no "todavía no lo configuró": son la misma ausencia de dato pero
   * el modal las separa con una tarjeta de opción explícita. */
  fechaVencimiento: string | null;
  /** CUÁNTOS recordatorios previos hay activos. Se pide el número y no el
   * arreglo porque los dos orígenes difieren: `recurso_documentos` trae
   * `notificar_dias: number[]` y el certificado de un EPP trae
   * `certificado_avisos: number`. Normalizar en el borde deja un solo camino
   * acá adentro. */
  avisos: number;
  /** El documento está marcado "No aplica" (sólo el catálogo de empresa). */
  noExigible?: boolean;
  /** Todavía no hay archivo subido. Manda sobre la fecha: sin archivo no hay
   * vigencia que mostrar, hay una fecha que se va a calcular sola al subir. */
  sinArchivo?: boolean;
  /**
   * Estado ya calculado por quien llama, cuando lo tiene. La API lo manda en
   * `estado_vencimiento` y ese valor MANDA sobre el que se derive acá: el
   * servidor mide "por vencer" con su propia ventana y si el cliente recalcula
   * con otra, la fila y el KPI de la misma pantalla se contradicen.
   */
  estado?: EstadoVencimiento;
}

export interface CeldaVigencia {
  estado: EstadoCeldaVigencia;
  /** Línea 1 — la fecha, o "No vence" / "No exigible". */
  fecha: string;
  /** Clase de color de la línea 1. */
  fechaTono: string;
  /** Línea 2 — plazo y recordatorios. `null` sólo en "No exigible". */
  detalle: string | null;
  detalleTono: string;
  /** Glifo de la línea 2. `null` cuando no hay línea 2. */
  icono: string | null;
  /** Si la celda acepta clic. "No exigible" queda inerte: no hay vigencia que
   * editar en algo que la empresa declaró que no le aplica. */
  editable: boolean;
}

/** "12 mar 2027" — mes abreviado en minúscula, como el resto de la app. */
function formatFecha(iso: string): string {
  const [anio, mes, dia] = iso.split("-").map(Number);
  return new Date(anio, mes - 1, dia)
    .toLocaleDateString("es-CL", { day: "numeric", month: "short", year: "numeric" })
    .replace(".", "");
}

/** "en 224 días" / "vence hoy" / "venció hace 47 días". */
function plazoEnPalabras(dias: number, vencido: boolean): string {
  if (vencido) {
    const atraso = Math.abs(dias);
    return atraso === 1 ? "venció ayer" : `venció hace ${atraso} días`;
  }
  if (dias === 0) return "vence hoy";
  if (dias === 1) return "vence mañana";
  return `en ${dias} días`;
}

/** "2 avisos" / "1 aviso" / "sin avisos". */
function avisosEnPalabras(avisos: number): string {
  if (avisos === 0) return "sin avisos";
  return avisos === 1 ? "1 aviso" : `${avisos} avisos`;
}

export function celdaVigencia({
  fechaVencimiento,
  avisos,
  noExigible = false,
  sinArchivo = false,
  estado,
}: EntradaCeldaVigencia): CeldaVigencia {
  if (noExigible) {
    return {
      estado: "no_exigible",
      fecha: "No exigible",
      fechaTono: "text-soft",
      detalle: null,
      detalleTono: "",
      icono: null,
      editable: false,
    };
  }

  if (sinArchivo) {
    return {
      estado: "sin_archivo",
      // DESVIACIÓN del DS, que rotula esta línea "Definir fecha" en teal. Acá
      // la celda NO es clickeable —sin archivo no hay vigencia que editar, la
      // fecha la calcula el sistema al subir— y el teal es el color de lo
      // apretable en toda la app. Un "Definir fecha" en teal que no responde al
      // clic es peor que decir la verdad: "Sin fecha".
      fecha: "Sin fecha",
      fechaTono: "text-soft",
      detalle: "al subir se calcula",
      // Ámbar: es lo único accionable de la fila, y lo accionable va primero.
      detalleTono: "text-warning",
      icono: "error_outline",
      editable: false,
    };
  }

  if (!fechaVencimiento) {
    return {
      estado: "permanente",
      fecha: "No vence",
      fechaTono: "text-body",
      detalle: "sin recordatorios",
      detalleTono: "text-soft",
      // Campana tachada y no ausencia de ícono: sin recordatorios ES el estado,
      // y una línea sin glifo se lee como que falta el dato.
      icono: "notifications_off",
      editable: true,
    };
  }

  const dias = diasHastaVencimiento(fechaVencimiento);
  const vencido = estado ? estado === "vencido" : dias < 0;
  const porVencer = estado ? estado === "por_vencer" : false;

  const fechaTono = vencido
    ? "text-destructive"
    : porVencer
      ? "text-warning"
      : "text-strong";

  // El ámbar de "sin avisos" NO pisa al rojo de vencido: en un documento ya
  // vencido no queda aviso que dar, así que denunciar la falta de
  // recordatorios sería tarde y además taparía lo urgente.
  const detalleTono = vencido
    ? "text-destructive"
    : avisos === 0
      ? "text-warning"
      : "text-meta";

  return {
    estado: vencido ? "vencido" : porVencer ? "por_vencer" : "vigente",
    fecha: formatFecha(fechaVencimiento),
    fechaTono,
    detalle: `${plazoEnPalabras(dias, vencido)} · ${avisosEnPalabras(avisos)}`,
    detalleTono,
    icono: avisos > 0 ? "notifications_active" : "notifications_off",
    editable: true,
  };
}
